import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FaArrowLeft, FaArrowRight, FaSave } from 'react-icons/fa';
import { Monitor, Smartphone } from 'lucide-react';
import { templateApi } from '../services/emailApi';
import { useToast } from '../shared/ToastProvider';
import BasicInfoSection from './sections/BasicInfoSection';
import HeaderSection from './sections/HeaderSection';
import BodySection from './sections/BodySection';
import FooterSection from './sections/FooterSection';
import TemplateWizardStepIndicator from './TemplateWizardStepIndicator';
import './TemplateManagement.css';

const defaultFormData = {
  templateName: '',
  templateCode: '',
  category: 'campaigns_marketing',
  channel: 'email',
  subject: '',
  headerLogoPosition: 'center',
  headerTitle: '',
  headerBackgroundColor: '#ffffff',
  headerLogoUrl: '',
  logoUrlFile: null,
  logoUrlPreview: '',
  headerBannerImageUrl: '',
  bannerImageUrlFile: null,
  bannerImageUrlPreview: '',
  bodyGreeting: '',
  bodyContent: '',
  bodyContentFormat: 'html',
  ctaButtonText: '',
  ctaButtonUrl: '',
  ctaButtonBackgroundColor: '#007bff',
  footerSignature: '',
  footerWebsiteUrl: '',
  footerSlogan: '',
  footerShowUnsubscribe: true,
  footerCompanyAddress: '',
  footerImageUrl: '',
  footerImageUrlFile: null,
  footerImageUrlPreview: '',
  unsubscribeUrl: '',
  socialLinks: { facebook: '', instagram: '', twitter: '', linkedin: '' },
  variableType: 'name',
  variables: [],
  isActive: true
};

/* ── Build full email HTML client-side for live preview ── */
const buildLivePreviewHtml = (data, attachmentsList = []) => {
  const logoUrl = data.logoUrlPreview || data.headerLogoUrl || '';
  const bannerUrl = data.bannerImageUrlPreview || data.headerBannerImageUrl || '';
  const footerImgUrl = data.footerImageUrlPreview || data.footerImageUrl || '';
  const bgColor = data.headerBackgroundColor || '#ffffff';
  const logoPos = data.headerLogoPosition || 'center';
  const social = data.socialLinks || {};

  // Replace variables with sample values for preview
  const replaceVars = (text) => {
    if (!text) return '';
    return text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return `<span style="background:#e0e7ff;color:#4338ca;padding:1px 4px;border-radius:3px;font-size:0.85em">${match}</span>`;
    });
  };

  let headerHtml = '';
  if (data.headerTitle || logoUrl || bannerUrl) {
    headerHtml = `<div style="background-color:${bgColor};padding:24px 20px;text-align:${logoPos};overflow:hidden">
      ${logoUrl ? `<img src="${logoUrl}" alt="Logo" style="max-height:60px;max-width:200px;width:auto;height:auto;display:${logoPos === 'center' ? 'block' : 'inline-block'};${logoPos === 'center' ? 'margin:0 auto' : logoPos === 'right' ? 'margin-left:auto' : ''}">` : ''}
      ${data.headerTitle ? `<h1 style="margin:12px 0 0;font-size:22px;color:#1e293b">${replaceVars(data.headerTitle)}</h1>` : ''}
      ${bannerUrl ? `<img src="${bannerUrl}" alt="Banner" style="width:100%;max-width:100%;height:auto;margin-top:16px;border-radius:8px;display:block;object-fit:contain;${logoPos === 'center' ? 'margin-left:auto;margin-right:auto' : ''}">` : ''}
    </div>`;
  }

  let bodyHtml = '<div style="padding:24px 20px">';
  if (data.bodyGreeting) {
    bodyHtml += `<p style="font-size:16px;color:#334155;margin:0 0 16px">${replaceVars(data.bodyGreeting)}</p>`;
  }
  bodyHtml += `<div style="color:#475569;line-height:1.7;font-size:15px">${replaceVars(data.bodyContent || '<p style="color:#94a3b8;font-style:italic">Start typing your email content...</p>')}</div>`;
  bodyHtml += '</div>';

  let footerHtml = '';
  const hasFooter = data.footerSignature || data.footerWebsiteUrl || data.footerSlogan || data.footerCompanyAddress || data.footerShowUnsubscribe || footerImgUrl || Object.values(social).some(Boolean);

  /* ── Attachments bar in preview ── */
  let attachmentsHtml = '';
  if (attachmentsList.length > 0) {
    const items = attachmentsList.map(a => {
      const sizeKb = a.size ? `(${(a.size / 1024).toFixed(0)} KB)` : '';
      return `<a href="${a.url}" target="_blank" rel="noreferrer" style="display:inline-flex;align-items:center;gap:4px;padding:4px 10px;background:#f1f5f9;border:1px solid #e2e8f0;border-radius:6px;font-size:12px;color:#334155;text-decoration:none;margin:2px 4px">📄 ${a.name} <span style="color:#94a3b8">${sizeKb}</span></a>`;
    }).join('');
    attachmentsHtml = `<div style="padding:12px 20px;border-top:1px solid #e5e7eb"><p style="font-size:13px;color:#64748b;margin:0 0 6px">📎 Attachments:</p><div style="display:flex;flex-wrap:wrap;gap:4px">${items}</div></div>`;
  }
  if (hasFooter) {
    footerHtml = '<div style="border-top:1px solid #e5e7eb;padding:24px 20px;text-align:center;background:#f8fafc">';
    if (footerImgUrl) {
      footerHtml += `<img src="${footerImgUrl}" alt="Footer Logo" style="max-height:40px;max-width:180px;width:auto;height:auto;margin-bottom:12px;display:block;margin-left:auto;margin-right:auto;object-fit:contain">`;
    }
    if (data.footerSignature) {
      footerHtml += `<p style="font-size:14px;color:#475569;margin:0 0 8px;white-space:pre-line">${data.footerSignature}</p>`;
    }
    if (data.footerSlogan) {
      footerHtml += `<p style="font-size:13px;color:#94a3b8;font-style:italic;margin:0 0 12px">${data.footerSlogan}</p>`;
    }

    const socialIcons = [];
    if (social.facebook) socialIcons.push(`<a href="${social.facebook}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Facebook</a>`);
    if (social.instagram) socialIcons.push(`<a href="${social.instagram}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Instagram</a>`);
    if (social.twitter) socialIcons.push(`<a href="${social.twitter}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">Twitter</a>`);
    if (social.linkedin) socialIcons.push(`<a href="${social.linkedin}" style="color:#4f46e5;text-decoration:none;margin:0 6px;font-size:13px">LinkedIn</a>`);
    if (socialIcons.length) {
      footerHtml += `<div style="margin:8px 0">${socialIcons.join(' · ')}</div>`;
    }

    if (data.footerWebsiteUrl) {
      footerHtml += `<p style="font-size:12px;margin:8px 0 0"><a href="${data.footerWebsiteUrl}" style="color:#4f46e5;text-decoration:none">${data.footerWebsiteUrl}</a></p>`;
    }
    if (data.footerCompanyAddress) {
      footerHtml += `<p style="font-size:12px;color:#94a3b8;margin:6px 0 0">${data.footerCompanyAddress}</p>`;
    }
    if (data.footerShowUnsubscribe) {
      footerHtml += `<p style="font-size:11px;margin:12px 0 0"><a href="#" style="color:#94a3b8;text-decoration:underline">Unsubscribe</a></p>`;
    }
    footerHtml += '</div>';
  }

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">
<style>body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;line-height:1.6;background:#f1f5f9}img{max-width:100%!important;height:auto!important;display:block;object-fit:contain;box-sizing:border-box}table{max-width:100%!important;table-layout:fixed}td img{max-width:100%!important;height:auto!important}a{color:#4f46e5}.email-container{max-width:600px;margin:0 auto;background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 1px 3px rgba(0,0,0,0.08);word-wrap:break-word;overflow-wrap:break-word}</style>
</head><body><div class="email-container">${headerHtml}${bodyHtml}${attachmentsHtml}${footerHtml}</div></body></html>`;
};

const EmailTemplateEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState(defaultFormData);
  const [errors, setErrors] = useState({});
  const [currentStep, setCurrentStep] = useState(1);
  const [stepsCompleted, setStepsCompleted] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!!id);
  const [previewMode, setPreviewMode] = useState('desktop'); // 'desktop' or 'mobile'
  const [bodyAttachments, setBodyAttachments] = useState([]);

  useEffect(() => {
    if (!id) {
      setFormData(defaultFormData);
      setCurrentStep(1);
      setStepsCompleted([]);
      return;
    }
    setInitialLoading(true);
    templateApi.getById(id)
      .then((res) => {
        const template = res.data?.data ?? res.data ?? {};
        /* ── Resolve variables from variables_v2, variables object, or array ── */
        const resolveVariables = () => {
          // Try variables_v2 first (has the actual variable data)
          const v2 = template.variables_v2;
          if (v2 && typeof v2 === 'object' && !Array.isArray(v2) && Object.keys(v2).length > 0) {
            return Object.entries(v2).map(([key, val]) => ({
              key, type: (val && typeof val === 'object' ? val.type : 'name') || 'name'
            }));
          }
          // Try variables as array
          const vars = template.variables;
          if (Array.isArray(vars) && vars.length > 0) return vars;
          // Try variables as object
          if (vars && typeof vars === 'object' && !Array.isArray(vars) && Object.keys(vars).length > 0) {
            return Object.entries(vars).map(([key, val]) => ({
              key, type: (val && typeof val === 'object' ? val.type : 'name') || 'name'
            }));
          }
          return defaultFormData.variables;
        };
        const resolvedVars = resolveVariables();
        setFormData({
          ...defaultFormData,
          ...template,
          templateName: template.templateName || template.template_name || template.name || '',
          templateCode: template.templateCode || template.template_code || '',
          // Always prefer template_uuid for API operations
          templateUuid: template.template_uuid || template.templateUuid || id,
          category: template.category || defaultFormData.category,
          channel: 'email',
          subject: template.subject || '',
          headerLogoPosition: template.headerLogoPosition ?? template.header?.logoPosition ?? defaultFormData.headerLogoPosition,
          headerTitle: template.headerTitle ?? template.header?.title ?? template.header_html ?? '',
          headerBackgroundColor: template.headerBackgroundColor ?? template.header?.backgroundColor ?? '#ffffff',
          logoUrlPreview: template.logoUrlPreview || template.headerLogoSignedUrl || template.headerLogoUrl || template.header?.logoUrl || '',
          bannerImageUrlPreview: template.bannerImageUrlPreview || template.headerBannerSignedUrl || template.headerBannerImageUrl || template.bannerImageSignedUrl || template.bannerImageUrl || template.header?.bannerImageUrl || '',
          bodyGreeting: template.bodyGreeting ?? template.body?.greeting ?? '',
          bodyContent: template.bodyContent ?? template.body?.content ?? '',
          bodyContentFormat: template.bodyContentFormat ?? template.body?.contentFormat ?? 'html',
          ctaButtonText: template.ctaButtonText || '',
          ctaButtonUrl: template.ctaButtonUrl || '',
          ctaButtonBackgroundColor: template.ctaButtonBackgroundColor || '#007bff',
          footerSignature: template.footerSignature ?? template.footer?.signature ?? '',
          footerWebsiteUrl: template.footerWebsiteUrl ?? template.footer?.websiteUrl ?? '',
          footerSlogan: template.footerSlogan ?? template.footer?.slogan ?? '',
          footerShowUnsubscribe: template.footerShowUnsubscribe ?? template.footer?.showUnsubscribe ?? true,
          footerCompanyAddress: template.footerCompanyAddress ?? template.footer?.companyAddress ?? '',
          footerImageUrlPreview: template.footerImageUrlPreview || template.footerImageSignedUrl || template.footerImageUrl || template.footer?.footerImageUrl || '',
          unsubscribeUrl: template.unsubscribeUrl ?? template.footer?.unsubscribeUrl ?? '',
          socialLinks: { ...defaultFormData.socialLinks, ...(template.socialLinks || template.footer?.socialLinks || {}) },
          variableType: template.variableType || template.variable_type || ((() => {
            if (resolvedVars.length > 0 && resolvedVars.every(v => /^\d+$/.test(v.key))) return 'number';
            return 'name';
          })()),
          variables: resolvedVars,
          isActive: template.isActive ?? template.is_active ?? (template.status === 'active') ?? defaultFormData.isActive
        });
        // Pre-populate attachments from saved template
        setBodyAttachments(template.bodyAttachments || []);
      })
      .catch((err) => {
        console.error('[TemplateEditor] Failed to load template:', err);
        toast?.error?.('Failed to load template');
        navigate('/dashboard/templates');
      })
      .finally(() => setInitialLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const steps = [
    { number: 1, title: 'Template Info', key: 'basicInfo' },
    { number: 2, title: 'Header', key: 'header' },
    { number: 3, title: 'Body', key: 'body' },
    { number: 4, title: 'Footer', key: 'footer' }
  ];

  const isStepCompleted = (stepNumber) => stepsCompleted.includes(stepNumber);
  const markStepComplete = (stepNumber) => {
    if (!stepsCompleted.includes(stepNumber)) setStepsCompleted((prev) => [...prev, stepNumber]);
  };
  const goToStep = (step) => { if (step >= 1 && step <= steps.length) setCurrentStep(step); };

  const validateCurrentStep = () => {
    const newErrors = {};
    if (currentStep === 1) {
      if (!formData.templateName?.trim()) newErrors.templateName = 'Template name is required';
      if (!formData.subject?.trim()) newErrors.subject = 'Subject line is required';
    }
    // Header title and greeting are optional per API spec
    if (currentStep === 3) {
      const bodyText = formData.bodyContent?.replace(/<[^>]*>/g, '').trim();
      if (!bodyText) newErrors.bodyContent = 'Email content is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goToNextStep = () => {
    if (!validateCurrentStep()) return;
    if (currentStep < steps.length) { markStepComplete(currentStep); setCurrentStep(currentStep + 1); }
  };
  const goToPrevStep = () => { if (currentStep > 1) { setErrors({}); setCurrentStep(currentStep - 1); } };
  const handleCancel = useCallback(() => navigate('/dashboard/templates'), [navigate]);

  const updateFormData = (updates) => {
    setFormData((prev) => ({ ...prev, ...updates }));
    const updatedKeys = Object.keys(updates);
    if (updatedKeys.some((key) => errors[key])) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        updatedKeys.forEach((key) => delete newErrors[key]);
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    // Only name, subject, bodyContent are required per API spec
    if (!formData.templateName?.trim()) newErrors.templateName = 'Template name is required';
    if (!formData.subject?.trim()) newErrors.subject = 'Subject line is required';
    const bodyText = formData.bodyContent?.replace(/<[^>]*>/g, '').trim();
    if (!bodyText) newErrors.bodyContent = 'Email content is required';
    setErrors(newErrors);
    if (newErrors.templateName || newErrors.subject) { setCurrentStep(1); }
    else if (newErrors.bodyContent) { setCurrentStep(3); }
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveClick = async () => {
    if (!validateForm()) return;
    setLoading(true);
    try {
      const {
        logoUrlFile, logoUrlPreview, bannerImageUrlFile, bannerImageUrlPreview,
        footerImageUrlFile, footerImageUrlPreview, templateUuid,
        ...cleanTemplateData
      } = formData;

      // Convert variables array to object format expected by API
      let variables = cleanTemplateData.variables;
      if (Array.isArray(variables)) {
        variables = variables.reduce((acc, { key }) => {
          if (key) acc[key] = { type: cleanTemplateData.variableType || 'name' };
          return acc;
        }, {});
      }

      // Build FLAT payload matching the API schema
      // templateCode is OPTIONAL — backend auto-generates if not sent
      const payload = {
        templateName: cleanTemplateData.templateName,
        ...(cleanTemplateData.templateCode?.trim() ? { templateCode: cleanTemplateData.templateCode.trim() } : {}),
        category: cleanTemplateData.category || 'general',
        channel: cleanTemplateData.channel || 'email',
        subject: cleanTemplateData.subject,
        isActive: cleanTemplateData.isActive,
        variableType: cleanTemplateData.variableType,
        variables,
        // Header fields — flat
        headerTitle: cleanTemplateData.headerTitle || '',
        headerBackgroundColor: cleanTemplateData.headerBackgroundColor || '#ffffff',
        headerLogoPosition: cleanTemplateData.headerLogoPosition || 'center',
        headerLogoUrl: cleanTemplateData.headerLogoUrl || '',
        headerBannerImageUrl: cleanTemplateData.headerBannerImageUrl || '',
        // Body fields — flat
        bodyGreeting: cleanTemplateData.bodyGreeting || '',
        bodyContent: cleanTemplateData.bodyContent || '',
        bodyContentFormat: cleanTemplateData.bodyContentFormat || 'html',
        // CTA Button fields
        ctaButtonText: cleanTemplateData.ctaButtonText || '',
        ctaButtonUrl: cleanTemplateData.ctaButtonUrl || '',
        ctaButtonBackgroundColor: cleanTemplateData.ctaButtonBackgroundColor || '#007bff',
        // Footer fields — flat
        footerSignature: cleanTemplateData.footerSignature || '',
        footerWebsiteUrl: cleanTemplateData.footerWebsiteUrl || '',
        footerSlogan: cleanTemplateData.footerSlogan || '',
        footerShowUnsubscribe: cleanTemplateData.footerShowUnsubscribe ?? true,
        footerCompanyAddress: cleanTemplateData.footerCompanyAddress || '',
        footerImageUrl: cleanTemplateData.footerImageUrl || '',
        unsubscribeUrl: cleanTemplateData.unsubscribeUrl || '',
        socialLinks: cleanTemplateData.socialLinks || defaultFormData.socialLinks,
        // Attachments — array of { name, url, size, type }
        bodyAttachments: bodyAttachments.length > 0 ? bodyAttachments : [],
      };

      // Always use template_uuid for API calls
      const templateUuidForApi = formData.templateUuid || id;
      let savedTemplateId = templateUuidForApi;

      if (id) {
        await templateApi.update(templateUuidForApi, payload);
        toast?.success?.('Template updated successfully!');
      } else {
        const res = await templateApi.create(payload);
        const created = res?.data?.data ?? res?.data ?? {};
        // Prefer template_uuid from response for subsequent image upload
        savedTemplateId = created.template_uuid || created.templateUuid || created.id;
        toast?.success?.('Template created successfully!');
      }

      // Upload images if any files were selected
      const hasImages = logoUrlFile || bannerImageUrlFile || footerImageUrlFile;
      if (hasImages && savedTemplateId) {
        try {
          const imageForm = new FormData();
          // Use field names matching the API spec: logoImage, bannerImage, footerImage
          if (logoUrlFile) imageForm.append('logoImage', logoUrlFile);
          if (bannerImageUrlFile) imageForm.append('bannerImage', bannerImageUrlFile);
          if (footerImageUrlFile) imageForm.append('footerImage', footerImageUrlFile);
          await templateApi.uploadImages(savedTemplateId, imageForm);
        } catch (imgErr) {
          console.warn('Image upload failed (template saved):', imgErr);
          toast?.warning?.('Template saved but image upload failed. You can re-upload images by editing the template.');
        }
      }

      navigate('/dashboard/templates');
    } catch (err) {
      console.error('Form submission error:', err);
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
      toast?.error?.(serverMsg || 'Failed to save template. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Live preview — smooth updates via contentDocument ── */
  const iframeRef = useRef(null);
  const iframeReady = useRef(false);

  const livePreviewHtml = useMemo(() => buildLivePreviewHtml(formData, bodyAttachments), [formData, bodyAttachments]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    // If iframe document is already open, write directly for smooth update
    if (iframeReady.current) {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          // Preserve scroll position
          const scrollTop = doc.documentElement?.scrollTop || doc.body?.scrollTop || 0;
          doc.open();
          doc.write(livePreviewHtml);
          doc.close();
          // Restore scroll position
          if (doc.documentElement) doc.documentElement.scrollTop = scrollTop;
          if (doc.body) doc.body.scrollTop = scrollTop;
        }
      } catch (e) {
        // Fallback: re-set srcDoc if direct write fails
        iframe.srcdoc = livePreviewHtml;
      }
    }
  }, [livePreviewHtml]);

  const handleIframeLoad = useCallback(() => {
    iframeReady.current = true;
  }, []);

  const previewSubject = useMemo(() => {
    if (!formData.subject) return '(no subject)';
    return formData.subject.replace(/\{\{(\w+)\}\}/g, (m) => m);
  }, [formData.subject]);

  if (initialLoading) {
    return (
      <div className="emtpl-loading">
        <div className="emtpl-spinner" />
        <p className="emtpl-loading-text">Loading template...</p>
      </div>
    );
  }

  return (
    <div className="emtpl-editor-layout">
      {/* ── Left: Form Panel ── */}
      <div className="emtpl-editor-form-panel">
        <form
          className="emtpl-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (currentStep < steps.length) { goToNextStep(); }
            else handleSaveClick();
          }}
        >
          <div className="emtpl-form-header">
            <button type="button" className="em-btn em-btn-secondary em-btn-icon" onClick={handleCancel} title="Back to templates">
              <FaArrowLeft size={14} />
            </button>
            <div>
              <h1 className="emtpl-form-title">{id ? 'Edit Template' : 'Create New Template'}</h1>
              <p className="emtpl-form-subtitle">Fill in the details to {id ? 'update' : 'create'} your template</p>
            </div>
          </div>

          <TemplateWizardStepIndicator
            steps={steps}
            currentStep={currentStep}
            onStepClick={goToStep}
            isStepCompleted={isStepCompleted}
            maxWidth="100%"
          />

          <div className="emtpl-form-container">
            {currentStep === 1 && (
              <BasicInfoSection formData={formData} errors={errors} onChange={updateFormData} />
            )}
            {currentStep === 2 && (
              <HeaderSection formData={formData} errors={errors} onChange={updateFormData} />
            )}
            {currentStep === 3 && (
              <BodySection formData={formData} errors={errors} onChange={updateFormData} attachments={bodyAttachments} onAttachmentsChange={setBodyAttachments} templateId={formData.templateUuid || id} />
            )}
            {currentStep === 4 && (
              <FooterSection formData={formData} errors={errors} onChange={updateFormData} />
            )}
          </div>

          <div className="emtpl-form-nav">
            <div className="emtpl-nav-group">
              <button type="button" className="em-btn em-btn-secondary" onClick={goToPrevStep} disabled={currentStep === 1}>
                <FaArrowLeft size={14} /> Previous
              </button>
            </div>
            <div className="emtpl-nav-group">
              {currentStep < steps.length ? (
                <button type="button" className="em-btn em-btn-primary" onClick={goToNextStep}>
                  Next <FaArrowRight size={14} />
                </button>
              ) : (
                <button type="button" className="em-btn em-btn-primary" disabled={loading} onClick={handleSaveClick}>
                  {loading ? 'Saving...' : (<><FaSave size={14} /> {id ? 'Update Template' : 'Create Template'}</>)}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* ── Right: Live Preview Panel ── */}
      <div className="emtpl-preview-panel">
        <div className="emtpl-preview-panel-header">
          <h3 className="emtpl-preview-panel-title">Live Preview</h3>
          <div className="emtpl-preview-device-toggle">
            <button
              type="button"
              className={`emtpl-device-btn${previewMode === 'desktop' ? ' active' : ''}`}
              onClick={() => setPreviewMode('desktop')}
              title="Desktop preview"
            >
              <Monitor size={14} />
            </button>
            <button
              type="button"
              className={`emtpl-device-btn${previewMode === 'mobile' ? ' active' : ''}`}
              onClick={() => setPreviewMode('mobile')}
              title="Mobile preview"
            >
              <Smartphone size={14} />
            </button>
          </div>
        </div>

        <div className="emtpl-preview-subject-bar">
          <strong>Subject:</strong> {previewSubject}
        </div>

        <div className={`emtpl-preview-frame emtpl-preview-${previewMode}`}>
          <iframe
            ref={iframeRef}
            title="Live Email Preview"
            sandbox="allow-same-origin"
            className="emtpl-live-iframe"
            srcDoc={livePreviewHtml}
            onLoad={handleIframeLoad}
          />
        </div>
      </div>
    </div>
  );
};

export default EmailTemplateEditor;
